import { useState, useEffect, useRef } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { Link } from 'react-router-dom';
import RelatedPages from '@/components/base/RelatedPages';

interface CountUpProps {
  target: number;
  suffix?: string;
  duration?: number;
}

function CountUp({ target, suffix = '', duration = 2000 }: CountUpProps) {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          const startTime = performance.now();
          const step = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

const milestones = [
  { year: '2003', title: '創立康永生技', desc: '由資深食品研發專家創立，深耕天然保健食品代工領域' },
  { year: '2008', title: 'GMP 廠房認證', desc: '正式取得 GMP 認證，生產規範全面升級' },
  { year: '2013', title: '突破 100 個合作品牌', desc: '服務品牌數突破百家，建立完善的 ODM 服務體系' },
  { year: '2018', title: '導入智慧化管理系統', desc: '引進 ERP 管理系統，提升生產追蹤與品質管控能力' },
  { year: '2022', title: '邁向 350+ 品牌夥伴', desc: '合作品牌累計超過 350 家，涵蓋電商、藥局、連鎖通路' },
];

const categoryExperiences = [
  { icon: 'ri-heart-pulse-line', label: '美容保養', count: '80+', desc: '美肌、水潤、抗氧化系列' },
  { icon: 'ri-basketball-line', label: '運動健身', count: '65+', desc: '肌力補充、體能維持系列' },
  { icon: 'ri-leaf-line', label: '女性保健', count: '90+', desc: '私密健康、更年期調養系列' },
  { icon: 'ri-mental-health-line', label: '睡眠紓壓', count: '55+', desc: '舒眠輔助、神經調適系列' },
  { icon: 'ri-seedling-line', label: '腸道健康', count: '70+', desc: '益生菌、消化輔助系列' },
  { icon: 'ri-star-line', label: '兒童成長', count: '40+', desc: '兒童鈣質、學習力系列' },
];

const testimonials = [
  {
    name: '王品牌創辦人',
    company: '某電商保健品牌',
    text: '康永從配方到包裝全程協助，讓我從零開始的品牌在 6 個月內成功上線，省去了非常多試錯的成本。',
    tag: '電商品牌',
  },
  {
    name: '李總監',
    company: '連鎖藥局採購',
    text: '與康永合作多年，最欣賞他們對法規的熟悉程度，每一個產品都讓我們上架無後顧之憂。',
    tag: '藥局通路',
  },
  {
    name: '陳執行長',
    company: '保健品牌集團',
    text: '少量可接單這點真的幫我們很多，測試期不需要壓大量庫存，確認暢銷後再放量，非常靈活。',
    tag: '多品牌集團',
  },
];

export default function ExperienceContent() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=successful%20health%20supplement%20brand%20launch%20celebration%2C%20diverse%20business%20team%20in%20modern%20office%2C%20product%20achievement%20wall%20with%20multiple%20award%20certificates%2C%20green%20accents%20corporate%20environment%2C%20natural%20lighting&width=1920&height=440&seq=exp-hero-bg-01&orientation=landscape"
            alt="服務經歷背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[
                { label: '首頁', href: '/' },
                { label: '服務經歷' },
              ]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              首頁 · 服務經歷
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              服務經歷
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              以穩定服務經驗，陪伴品牌從產品想法走向市場。
            </p>
          </div>
        </div>
      </section>

      {/* Key stats with count-up */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              核心數據
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              數字背後是每一份信任
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              20 年深耕保健食品代工，用數字說明我們的專業積累
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                target: 350,
                suffix: '+',
                label: '合作服務品牌數',
                desc: '涵蓋電商、藥局、直銷、連鎖通路等多元品牌',
                icon: 'ri-store-2-line',
              },
              {
                target: 1000,
                suffix: '+',
                label: '生產總產品數',
                desc: '橫跨美容保養、運動健身、腸道健康等多個品類',
                icon: 'ri-flask-line',
              },
              {
                target: 95,
                suffix: '%',
                label: '客戶滿意好評',
                desc: '真實回饋來自多年合作品牌的持續信任與口碑推薦',
                icon: 'ri-star-smile-line',
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-[#f7faf7] rounded-2xl p-8 border border-gray-100 text-center hover:border-green-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-2xl border border-green-100 mx-auto mb-5">
                  <i className={`${stat.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <div className="text-[#4CAF50] font-bold text-[3rem] md:text-[3.5rem] leading-none mb-2">
                  <CountUp target={stat.target} suffix={stat.suffix} />
                </div>
                <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{stat.label}</div>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category experience */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              產品類別經驗
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              涵蓋多元保健品類
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              豐富的跨品類開發經驗，無論您的市場定位為何，都能找到適合的合作方案
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {categoryExperiences.map((cat) => (
              <div
                key={cat.label}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl border border-green-100 flex-shrink-0">
                    <i className={`${cat.icon} text-[#4CAF50] text-xl`} />
                  </div>
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[1rem]">{cat.label}</div>
                    <div className="text-[#4CAF50] font-bold text-[0.875rem]">{cat.count} 個產品</div>
                  </div>
                </div>
                <p className="text-gray-500 text-[0.875rem] leading-[1.7]">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              發展歷程
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              20 年穩健成長軌跡
            </h2>
          </div>
          <div className="relative">
            {/* Center line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-100 -translate-x-1/2" />
            <div className="space-y-8 md:space-y-0">
              {milestones.map((m, idx) => (
                <div
                  key={m.year}
                  className={`flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                    <div className={`bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300 ${idx % 2 === 0 ? 'md:ml-auto md:max-w-md' : 'md:mr-auto md:max-w-md'}`}>
                      <div className="text-[#4CAF50] font-bold text-[1.375rem] mb-2">{m.year}</div>
                      <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{m.title}</div>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.7]">{m.desc}</p>
                    </div>
                  </div>
                  {/* Dot */}
                  <div className="hidden md:flex w-16 flex-shrink-0 items-center justify-center z-10">
                    <div className="w-4 h-4 rounded-full bg-[#4CAF50] border-4 border-white ring-2 ring-green-200" />
                  </div>
                  <div className="md:w-[calc(50%-2rem)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              品牌聲音
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              合作品牌真實回饋
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.85] mb-6 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[0.9375rem]">{t.name}</div>
                    <div className="text-gray-400 text-[0.8125rem]">{t.company}</div>
                  </div>
                  <span className="text-[0.75rem] text-[#4CAF50] bg-green-50 border border-green-100 px-2.5 py-1 rounded-full">{t.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            成為下一個成功案例
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            無論您是剛起步的新品牌還是尋求擴展的成熟企業，康永都能提供合適的合作方案。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              立即洽詢代工服務
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/about/cases"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              查看成功案例
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '成功案例', desc: '看看康永協助品牌從零到上市的真實全程代工案例。', href: '/about/cases' },
        { title: '合作流程', desc: '從需求對焦到完成交貨，了解清楚可控的合作步驟。', href: '/cooperation/process' },
        { title: '立即洽詢', desc: '填寫諮詢表單，成為下一個康永的成功合作品牌。', href: '/contact/form' },
      ]} />
    </>
  );
}
